import {
  Checkbox,
  Grid,
  IconButton,
  IconColor,
  IconName,
  IconSize,
  LinkButton,
  Select,
  SelectIconColor,
  SelectOption,
  SelectVariant,
  Text,
  TextHighlight,
  TextSize,
  TextWeight
} from '@focaldata/cin-ui-components'
import React from 'react'
import { Operator } from '../../data/model/common'
import useStyles from './audienceConditionRows.styles'
import {
  ConditionRow,
  SourceQuestion,
  toSelectOptions
} from './audienceConditions'

const negationOptions: SelectOption[] = [
  { id: '0', name: 'is one of', value: 'false' },
  { id: '1', name: 'is not one of', value: 'true' }
]

const operatorOptions: SelectOption[] = [
  { id: '0', name: 'AND', value: Operator.And },
  { id: '1', name: 'OR', value: Operator.Or }
]

export const emptyRow = (negated: boolean): ConditionRow => ({
  questionId: '',
  responseOptionIds: [],
  negated
})

interface Props {
  rows: ConditionRow[]
  questions: SourceQuestion[]
  onChange: (rows: ConditionRow[]) => void
  /** Omitted by screening rules, which are an AND of separate conditions. */
  operator?: Operator
  onOperatorChange?: (operator: Operator) => void
  ariaPrefix: string
  defaultNegated: boolean
}

// The operator is editable on the first separator only: storage is an AND of OR-groups, never a mix.
const AudienceConditionRows: React.FC<Props> = ({
  rows,
  questions,
  onChange,
  operator,
  onOperatorChange,
  ariaPrefix,
  defaultNegated
}: Props) => {
  const { classes } = useStyles()
  const questionOptions = toSelectOptions(questions)
  const joiner = operator === Operator.Or ? 'OR' : 'AND'

  const updateRow = (index: number, next: Partial<ConditionRow>) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...next } : row)))

  return (
    <>
      {rows.map((row, index) => {
        const question = questions.find((q) => q.questionId === row.questionId)

        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional and have no stable id
          <React.Fragment key={index}>
            {index === 1 && operator !== undefined && onOperatorChange ? (
              <Grid className={classes.operatorContainer}>
                <Select
                  ariaLabel={`${ariaPrefix} operator`}
                  variant={SelectVariant.Underlined}
                  iconColor={SelectIconColor.Secondary}
                  width={70}
                  textSize={TextSize.ms}
                  selectedTextHighlight={TextHighlight.Standard}
                  selectedTextWeight={TextWeight.SemiBold}
                  options={operatorOptions}
                  optionValue={operator}
                  defaultOptionValue={operator}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    onOperatorChange(event.target.value as Operator)
                  }
                />
              </Grid>
            ) : (
              index > 0 && (
                <Grid className={classes.operatorContainer}>
                  <Text
                    size={TextSize.ms}
                    highlight={TextHighlight.Standard}
                    weight={TextWeight.SemiBold}
                  >
                    {joiner}
                  </Text>
                </Grid>
              )
            )}

            <Grid container className={classes.conditionContainer}>
              <Grid container justifyContent="flex-end">
                <Grid item xs="auto">
                  {rows.length > 1 && (
                    <IconButton
                      iconName={IconName.DeleteOutline}
                      iconSize={IconSize.Medium}
                      iconColor={IconColor.Emphasis}
                      ariaLabel="Delete condition"
                      onClick={() =>
                        onChange(rows.filter((_, i) => i !== index))
                      }
                    />
                  )}
                </Grid>
              </Grid>

              <Grid container gap="12px" wrap="nowrap" alignItems="flex-start">
                <Grid item xs={7}>
                  <Grid
                    container
                    gap="12px"
                    wrap="nowrap"
                    alignItems="center"
                    className={classes.selectsRow}
                  >
                    <Grid item xs={7}>
                      <Select
                        ariaLabel={`${ariaPrefix} question`}
                        placeholder="Please select…"
                        variant={SelectVariant.Filled}
                        options={questionOptions}
                        optionValue={row.questionId}
                        defaultOptionValue={row.questionId}
                        maxMenuHeight={300}
                        fullWidth
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) =>
                          updateRow(index, {
                            questionId: event.target.value,
                            responseOptionIds: []
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={5}>
                      <Select
                        ariaLabel={`${ariaPrefix} negation`}
                        variant={SelectVariant.Filled}
                        options={negationOptions}
                        optionValue={String(row.negated)}
                        fullWidth
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) =>
                          updateRow(index, {
                            negated: event.target.value === 'true'
                          })
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs className={classes.optionsContainer}>
                  {question?.responseOptions.map((option) => (
                    <Grid container key={option.id}>
                      <Checkbox
                        label={option.value}
                        checked={row.responseOptionIds.includes(option.id)}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) =>
                          updateRow(index, {
                            responseOptionIds: event.target.checked
                              ? [...row.responseOptionIds, option.id]
                              : row.responseOptionIds.filter(
                                  (id) => id !== option.id
                                )
                          })
                        }
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </React.Fragment>
        )
      })}

      <Grid className={classes.linkContainer}>
        <LinkButton
          highlight={TextHighlight.Emphasis}
          size={TextSize.m}
          weight={TextWeight.SemiBold}
          noPadding
          onClick={() => onChange([...rows, emptyRow(defaultNegated)])}
        >
          Add condition
        </LinkButton>
      </Grid>
    </>
  )
}

export default AudienceConditionRows
